import { todayPT } from "@homebound/activesupport";
import { Css } from "@homebound/beam";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  BidContractType,
  SaveBidContractInput,
  SavePlanBasedBidContractLineItemInput,
} from "src/generated/graphqlTypes";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { fail, pushInNextLoop, queryResult } from "src/utils";
import { PlanBasedBidsTableV2 } from "./components/PlanBasedBidsTableV2";
import {
  EnterPlanBasedBidPage_BidPackageFragment,
  useEnterPlanBasedBidPage_SaveBidContractMutation,
  useEnterPlanBasedBidPageQuery,
} from "./EnterPlanBasedBidPage.generated";

export function EnterPlanBasedBidPage() {
  const { bidPackageId, tradePartnerId } = useParams<{ bidPackageId: string; tradePartnerId: string }>();
  const query = useEnterPlanBasedBidPageQuery({ variables: { bidPackageId, tradePartnerId } });
  return queryResult(query, ({ bidPackage, tradePartner }) => (
    <EnterPlanBasedBidPageView bidPackage={bidPackage} tradePartner={tradePartner} />
  ));
}

type EnterPlanBasedBidPageViewProps = {
  bidPackage: EnterPlanBasedBidPage_BidPackageFragment;
  tradePartner: NamedFragment;
};

function EnterPlanBasedBidPageView(props: EnterPlanBasedBidPageViewProps) {
  const { bidPackage, tradePartner } = props;
  const history = useHistory();
  const existingBcr = bidPackage.latestVersion.latestBidContractRevisions.find(
    (b) => b.bidContract.tradePartner?.id === tradePartner.id,
  );
  const bidRequest = bidPackage.activeRequests.find((r) => r.tradePartner.id === tradePartner.id);

  const [saveBidContract] = useEnterPlanBasedBidPage_SaveBidContractMutation();

  const onSave = useCallback(
    async (planBasedLineItems: SavePlanBasedBidContractLineItemInput[]) => {
      // For an existing bid contract, just attach line items via id; the resolver writes them to the latest revision.
      // For a new one, send the BC creation fields - the resolver auto-creates the initial revision in the same call.
      const existingBidContractId = existingBcr?.bidContract.id;
      const input: SaveBidContractInput = existingBidContractId
        ? { id: existingBidContractId, planBasedLineItems }
        : {
            tradePartnerId: tradePartner.id,
            type: BidContractType.UserCreatedGroupBased,
            bidRequestId: bidRequest?.id ?? fail(`Bid request not found for trade partner ${tradePartner.id}`),
            startDate: todayPT(),
            planBasedLineItems,
          };
      await saveBidContract({ variables: { input } });
      pushInNextLoop(history, createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id));
    },
    [existingBcr, bidPackage, saveBidContract, tradePartner, bidRequest, history],
  );

  return (
    <>
      <div css={Css.xl2.tac.p2.fs0.$}>Enter Bid Information</div>
      <PlanBasedBidsTableV2 bidPackage={bidPackage} mode="edit" tradePartnerId={tradePartner.id} onSave={onSave} />
    </>
  );
}
